import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Link } from 'react-router-dom';

// Shared UI Library Components
import { CustomIcon, EIconName } from '@ppmcore/seven-ppm-core-shared-components-react';

// Styles
import './cost-per-session.scss';

// Components
import { CostSessionItem, TCostSessionItemRef } from '../cost-session-item/cost-session-item';
import {
  DeleteCostPerSessionModal
} from '../../../../modals/delete-cost-per-session-modal/delete-cost-per-session-modal';

// Store entities
import {
  createCounsellorSessionRate, deleteCounsellorSessionRate,
  getCostPerSessionRates, updateCounsellorSessionRate
} from '../../../../store/current-counsellor/current-counsellor.thunks';
import { openNotification } from '../../../../store/app-notifications/app-notifications.thunks';

// Models
import {
  ICostPerSessionCreate,
  ICostPerSessionRates, ICostPerSessionUpdate, ICounsellor,
  IPerSessionRateItem
} from '../../../../interfaces/counsellors.interfaces';
import { ICurrency } from '../../../../interfaces/company.interfaces';

export type TCostPerSessionRef = {
  isHasChanges: () => boolean;
  resetState: () => void;
};

type TCostPerSessionProps = {
  counsellor: ICounsellor;
  session_rates: ICostPerSessionRates;
  is_stripe_connected: boolean;
  percentFee: number;
  transactionFee: number;
  currency: ICurrency;
};

export const CostPerSession = forwardRef<TCostPerSessionRef, TCostPerSessionProps>((
    { counsellor, session_rates, is_stripe_connected, percentFee, transactionFee, currency }: TCostPerSessionProps, ref
  ) => {
    const itemsRef = useRef<Array<TCostSessionItemRef | null>>([]);

    const [deleteOpen, setDeleteOpen] = useState<boolean>(false);
    const [toggleLastOpen, setToggleLastOpenOpen] = useState<boolean>(false);
    const [activeSession, setActiveSession] = useState<{
      idx: number, session: IPerSessionRateItem | null
    } | null>(null);
    const [activeInfo, setActiveInfo] = useState<ICostPerSessionUpdate | null>(null);
    const [dataRows, setDataRows] = useState<Array<IPerSessionRateItem | null>>([]);

    const sortedDataRows = [...dataRows]
      .sort((a, b) => {
        if (a?.rate_type === 'audio_call' && b?.rate_type === 'audio_call') {
          return ((a?.call_duration || 0) - (b?.call_duration || 0));
        }

        if (a?.rate_type === 'video_call' && b?.rate_type === 'video_call') {
          return ((a?.call_duration || 0) - (b?.call_duration || 0));
        }

        if (a?.rate_type === 'conversation' && b?.rate_type === 'conversation') {
          return ((a?.call_duration || 0) - (b?.call_duration || 0));
        }

        return 1;
      });

    const dispatch = useDispatch<any>();

    const durations = {
      audio_call: session_rates.available_audio_call_duration,
      video_call: session_rates.available_video_call_duration,
      conversation: session_rates.available_conversation_duration,
    }

    const existDurations = {
      audio_call: session_rates.audio_call_per_session_rate.map(s => s.call_duration),
      video_call: session_rates.call_per_session_rate.map(s => s.call_duration),
      conversation: session_rates.conversation_per_session_rate.map(s => s.call_duration),
    }

    const hasEmptyRow = dataRows.some(row => !row?.id);
  const allSessionCreated =
    durations.audio_call.length + durations.video_call.length + durations.conversation.length ===
    existDurations.audio_call.length + existDurations.video_call.length + existDurations.conversation.length;

    const toggleDeleteModal = (): void => {
      setDeleteOpen(!deleteOpen);
    }

    const addMoreSession = (): void => {
      setDataRows(prevState => ([...prevState, ...(!!prevState.length ? [null] : [null, null])]));
    }

    const removeSession = (idx: number): void => {
      setDataRows(prevState => prevState.filter((_, i) => i !== idx));
    }

    const createNewSession = async (createInfo: ICostPerSessionCreate): Promise<{ payload: ICostPerSessionRates | null }> => {
      if (!is_stripe_connected) {
        dispatch(openNotification({
          type: 'error',
          description: 'Please connect your Stripe account first to deactivate the free services'
        }));
        return { payload: null };
      }
      return dispatch(createCounsellorSessionRate(createInfo));
    }

    const toggleSession = (updateInfo: ICostPerSessionUpdate, checked: boolean, event: any): void => {
      if (!checked && counsellor.rates_enabled_count === 1) {
        setToggleLastOpenOpen(true);
        setActiveInfo(updateInfo);
        event.preventDefault();
        return;
      }
      dispatch(updateCounsellorSessionRate(updateInfo));
    }

    const updateSession = (updateInfo: ICostPerSessionUpdate): Promise<{ payload: ICostPerSessionRates }> => {
      return dispatch(updateCounsellorSessionRate(updateInfo));
    }

    const deleteSession = (idx: number, session: IPerSessionRateItem | null): void => {
      if (!session && idx === 0) return;
      if (!session) {
        removeSession(idx);
        return;
      }
      toggleDeleteModal();
      setActiveSession({
        idx, session
      });
    }

    const deleteSessionSubmitHandler = (state: boolean): void => {
      if (!state) {
        toggleDeleteModal();
        return;
      }
      if (!activeSession?.session) return;
      toggleDeleteModal();
      dispatch(deleteCounsellorSessionRate({
        worker_id: counsellor.id,
        rate_id: activeSession?.session.id
      }));
    }

    const toggleLastOpenSessionSubmitHandler = (state: boolean): void => {
      setToggleLastOpenOpen(false);
      if (!state || !activeInfo) {
        setActiveInfo(null);
        return;
      }
      dispatch(updateCounsellorSessionRate(activeInfo));
      setActiveInfo(null);
    }

    const loadCostPerSessionRates = (): void => {
      dispatch(getCostPerSessionRates(counsellor.id));
    }

    useImperativeHandle(ref, () => ({
      isHasChanges(): boolean {
        return itemsRef.current.some(el => el?.isHasChanges());
      },
      resetState(): void {
        itemsRef.current.forEach(el => el?.resetState());
        setDataRows(prevState => prevState.filter(row => row !== null));
      }
    }));

    useEffect(() => {
      setDataRows(_ => [
        ...session_rates.audio_call_per_session_rate,
        ...session_rates.call_per_session_rate,
        ...session_rates.conversation_per_session_rate,
      ]);
      itemsRef.current.map(session => session?.resetState());
    }, [session_rates]);

    useEffect(() => {
      loadCostPerSessionRates();
    }, []);

    return (
      <div className="cost-per-session">
        <DeleteCostPerSessionModal open={ deleteOpen }
                                   isLastItem={ counsellor?.rates_enabled_count === 1 }
                                   onClose={ deleteSessionSubmitHandler }
        />
        <DeleteCostPerSessionModal open={ toggleLastOpen }
                                   isLastItem={ counsellor?.rates_enabled_count === 1 }
                                   onClose={ toggleLastOpenSessionSubmitHandler }
        />

        { (!durations.audio_call.length && !durations.video_call.length) &&
          <div className="cost-per-session--empty-time-slots">
            Set up Session time slots in the Business Settings first to Add Session and Cost. Click <Link
            to={ '/business-profile/consultations-settings' }>here</Link>.
          </div> }

        { (!!durations.audio_call.length || !!durations.video_call.length) &&
          <>
            <div className="cost-per-session--description">
              In this section, you can set the cost for Instant audio and video call sessions. The cost per session for
              these calls is adjustable for both types of consultation. As an Administrator, you have the flexibility to
              configure custom sessions with varying durations and costs. Each session comes with a control feature,
              allowing you to on or off the session as needed. The switcher's off-state indicates that the session is
              unavailable for Clients in the Consultant's profile.
            </div>

            <div className="cost-per-session--description">
              When setting Service Costs, consider the service charges you wish to receive as a business. The system
              assists you by showing the Credit Card Processing Fee in relation to your set service cost. No hidden or
              extra charges. This enables transparency in pricing for you.
            </div>

            {/*<div className="price-per-minute--notification">*/ }
            {/*  <CustomIcon name={ EIconName.Info } skipSize={ true }/> Each Minute-Based Call is subject to a*/ }
            {/*  fixed { currency.symbol }{ transactionFee } { currency.name } transaction fee charged by the Payment*/ }
            {/*  System,*/ }
            {/*  regardless of call length. This fee is not included in the*/ }
            {/*  Payable Amount for Clients per min.*/ }
            {/*</div>*/ }

            <div className="cost-per-session--edit-price">
              {
                !dataRows?.length &&
                <CostSessionItem worker_id={ counsellor.id }
                                 currency={ currency }
                                 percentFee={ percentFee }
                                 transactionFee={ transactionFee }
                                 isNullishItem={ true }
                                 durations={ durations }
                                 createSession={ createNewSession }
                />
              }
              {
                sortedDataRows.map((session, idx) =>
                  <CostSessionItem
                    ref={ el => itemsRef.current[idx] = el }
                    key={ idx }
                    currency={ currency }
                    worker_id={ counsellor.id }
                    percentFee={ percentFee }
                    transactionFee={ transactionFee }
                    durations={ durations }
                    existDurations={ existDurations }
                    isFirstItem={ idx === 0 }
                    deleteSession={ () => deleteSession(idx, session ?? null) }
                    createSession={ createNewSession }
                    updateSession={ updateSession }
                    toggleSession={ toggleSession }
                    { ...(session && { session }) }
                  />)
              }
            </div>

            { (!hasEmptyRow && !!dataRows?.length && !allSessionCreated) &&
              <div className="cost-per-session--adding" onClick={ addMoreSession }>
                <CustomIcon name={ EIconName.AddCircle }/> Add More
              </div> }
          </>
        }

      </div>
    );
  }
)
